import { Collaborator } from 'types/collaborator.types';
import {
  DDL,
  NumberValidation,
  StringValidation,
  Time,
} from 'types/general.types';
import { Link } from 'types/api.types';
import { LyricsData, LyricSheet2, LyricsVersion } from 'types/lyrics.types';
import { Audio } from 'types/audio';
import { ContributionsList } from 'types/contribution';
import { EnumValidation } from 'types/generic.types';
import { EntityType } from 'types/enums';
import { SearchableProfile } from 'types/notification.types';

export type SongSortByColumns = 'created' | 'updated' | 'title';

export enum SongStatusType {
  IN_CREATION = 'IN_CREATION',
  PITCHABLE = 'PITCHABLE',
  HOLD = 'HOLD',
  RELEASED = 'RELEASED',
}

export const SongStatusLabel = {
  [SongStatusType.IN_CREATION]: 'In Creation',
  [SongStatusType.PITCHABLE]: 'Pitchable',
  [SongStatusType.HOLD]: 'Hold',
  [SongStatusType.RELEASED]: 'Released',
};

export interface _SongsLinks {
  self: Link;
  create: Link;
  song: Link;
  next?: Link;
}

export interface _SongLinks {
  self: Link;
  updateSong: Link;
  createContribution: Link;
  listContributions: Link;
  deleteContribution: Link;
  getContribution: Link;
  getAudioStatus: Link;
  getAudio: Link;
  updateContribution: Link;
  createAudio: Link;
  deleteAudio: Link;
  downloadAudio: Link;
  listAudio: Link;
  updateAudio: Link;
  leaveSong?: Link;
  listCredits?: Link;
  inviteCollaborator?: Link;
  countryCodesAlphaDDL?: Link;
  playAudio?: Link;
  getMyContributionCheckInCode?: Link;
  createContributionCheckInCode?: Link;
  putLatestVersion?: Link;
  playLatestVersion?: Link;
  deleteLatestVersion?: Link;
  getLatestWorkSplit?: Link;
  listContributionsForWorkSplit?: Link;
  createSplit?: Link;
  setLyricSheetVersion?: Link;
  listLyricSheets?: Link;
  getImportRecord?: Link;
}

export interface Genre {
  value: string;
  title: string;
}

export interface SongDDL {
  dateAccuracy: DDL;
  genres: DDL;
  keySignatures: DDL;
  statuses: DDL;
  timeSignatures: DDL;
}

export interface KeySignatureMetadata {
  isMajor: boolean;
  isSharp: boolean;
  signs: number;
}

export interface TimeSignatureMetadata {
  nom: number;
  denom: number;
}

const MAJOR = 'Major';
const MINOR = 'Minor';
const SHARP = 'Sharp';
const FLAT = 'Flat';

interface KeyOSignatureObject {
  [signs: number]: string;
}

export const MajorSharpKeys: KeyOSignatureObject = {
  0: `C${MAJOR}`,
  1: `G${MAJOR}`,
  2: `D${MAJOR}`,
  3: `A${MAJOR}`,
  4: `E${MAJOR}`,
  5: `B${MAJOR}`,
  6: `F${SHARP}${MAJOR}`,
  7: `C${SHARP}${MAJOR}`,
};

export const MajorFlatKeys: KeyOSignatureObject = {
  0: `C${MAJOR}`,
  1: `F${MAJOR}`,
  2: `B${FLAT}${MAJOR}`,
  3: `E${FLAT}${MAJOR}`,
  4: `A${FLAT}${MAJOR}`,
  5: `D${FLAT}${MAJOR}`,
  6: `G${FLAT}${MAJOR}`,
  7: `C${FLAT}${MAJOR}`,
};

export const MinorSharpKeys: KeyOSignatureObject = {
  0: `A${MINOR}`,
  1: `E${MINOR}`,
  2: `B${MINOR}`,
  3: `F${SHARP}${MINOR}`,
  4: `C${SHARP}${MINOR}`,
  5: `G${SHARP}${MINOR}`,
  6: `D${SHARP}${MINOR}`,
  7: `A${SHARP}${MINOR}`,
};
export const MinorFlatKeys: KeyOSignatureObject = {
  0: `A${MINOR}`,
  1: `D${MINOR}`,
  2: `G${MINOR}`,
  3: `C${MINOR}`,
  4: `F${MINOR}`,
  5: `B${FLAT}${MINOR}`,
  6: `E${FLAT}${MINOR}`,
  7: `A${FLAT}${MINOR}`,
};

export interface BaseSongListQueryParams {
  limit: NumberValidation;
  offset: NumberValidation;
  search: StringValidation;
}

export interface SongListQueryParams extends BaseSongListQueryParams {
  sortBy: EnumValidation<SongSortByColumns>;
  sortOrder: EnumValidation<'asc' | 'desc'>;
}

export type ChatChannelStatus = 'PENDING' | 'READY';

export interface ChatChannel {
  guid: string;
  status: ChatChannelStatus;
}

export interface FolderItem {
  guid: string;
  title: string;
  songCount?: number;
  created?: Date;
  updated?: Date;
}

export interface Folder {
  meta?: {
    totalCount: number;
  };
  items?: FolderItem[];
  item?: FolderItem;
  song?: {
    items: Song[];
    _links?: {
      next: Link;
    };
  };
  _links?: {
    self: Link;
    createFolder: Link;
    next: Link;
  };
}

export interface Song {
  guid: string;
  title: string;
  altTitle?: string;
  displayArtistName?: string;
  language?: string;
  keySignature?: string;
  timeSignature?: string;
  tempo?: number;
  duration?: number;
  explicit?: boolean;
  location?: {
    address: string | null;
  };
  notes?: string;
  collaborators?: Collaborator[];
  usualSuspects?: SearchableProfile[];
  creationDate?: { date: string };
  created: Time;
  updated: Time;
  status: SongStatusType;
  createdBy: string;
  _links?: _SongLinks;
  contributions?: ContributionsList;
  audio?: Audio[];
  genres?: string[];
  lyrics?: LyricsData[];
  lyricsVersion?: {
    version?: LyricsVersion;
  };
  latestVersionAudio?: string;
  lyricSheet?: LyricSheet2;
  entityType?: EntityType.SONG;
  chat?: ChatChannel;
  folder?: Folder;
}
